import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Github, Linkedin, Twitter, Code, Palette, Lightbulb, Rocket, Globe, HelpCircle } from "lucide-react";
import { DiscordIcon } from "@/components/icons/DiscordIcon";
import { ThreadsIcon } from "@/components/icons/ThreadsIcon";
import { SEO } from "@/components/seo/SEO";
import { PageHero } from "@/components/layout/PageHero";
import { ContentPage, ContentSection } from "@/components/layout/ContentPage";
import { hairlineClass, metaChipClass, slateCardClass } from "@/lib/card-styles";
import { typeBodyClass, typeProseClass, typeSubtitleClass } from "@/lib/typography";

const About = () => {
  const navigate = useNavigate();
  const technicalSkills = [
    "React & TypeScript",
    "Vite & Next.js",
    "PostgreSQL",
    "AI/LLM Integration",
    "Full-stack Development",
    "API Design",
    "Real-time Applications",
  ];

  const designSkills = [
    "UI/UX Design",
    "Responsive & Mobile-first",
    "Accessibility (a11y)",
    "Design Systems",
    "User Research",
    "Product Strategy",
  ];

  const socialLinks = [
    { icon: Github, href: "https://github.com/lyderdev", label: "GitHub" },
    { icon: Linkedin, href: "https://www.linkedin.com/in/l%E1%BB%A3i-v%C4%83n-645a09222/", label: "LinkedIn" },
    { icon: Twitter, href: "https://x.com/lyder_dev", label: "X (Twitter)" },
    { icon: ThreadsIcon, href: "https://www.threads.com/@loicoder", label: "Threads" },
    { icon: DiscordIcon, href: "https://discord.gg/ACj3Ncag", label: "Discord" },
  ];

  return (
    <>
      <SEO
        title="About Lyder - Developer & Creator"
        description="Learn about the developer behind Lyder Talk, an AI-powered speaking practice platform. Discover skills, expertise, and current focus in web development and language learning technology."
        url="/about"
        keywords="Lyder Talk developer, web developer, language learning technology, AI speaking practice, full-stack developer"
      />
      <ContentPage
        title="About Lyder"
        eyebrow="The person behind it"
        icon={Code}
        description="Developer and creator of Lyder Talk."
      >
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2 border-primary/30">
            <AvatarImage src="/ghibli-illustration.png" alt="Lyder" />
            <AvatarFallback className="bg-primary-strong text-xl font-bold">
              L
            </AvatarFallback>
          </Avatar>
          <p className={typeBodyClass}>
            Hi — I build Lyder Talk. As a language learner myself, I wanted a way to
            practise speaking that gave real feedback without needing a partner at
            every session.
          </p>
        </div>

        <ContentSection title="What I'm building" icon={Globe}>
          <p>
            Lyder Talk combines AI generation with language-learning practice loops.
            It covers 16 topic categories, and spans AI-generated topics, vocabulary
            building, study groups, and community sharing — all aimed at making
            speaking practice something you can actually keep doing.
          </p>
        </ContentSection>

        <ContentSection title="Skills & expertise" icon={Lightbulb}>
          <div className="space-y-4">
            <div>
              <h3 className={cn(typeSubtitleClass, "mb-2 flex items-center gap-2")}>
                <Code className="h-4 w-4 text-primary" aria-hidden="true" />
                Technical
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {technicalSkills.map((skill) => (
                  <span key={skill} className={metaChipClass}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            <div className={cn("border-t pt-4", hairlineClass)}>
              <h3 className={cn(typeSubtitleClass, "mb-2 flex items-center gap-2")}>
                <Palette className="h-4 w-4 text-primary" aria-hidden="true" />
                Design
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {designSkills.map((skill) => (
                  <span key={skill} className={metaChipClass}>
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </ContentSection>

        <ContentSection title="Current focus" icon={Rocket}>
          <p>
            Making the practice loop tighter: faster feedback on spoken answers,
            vocabulary that actually sticks through spaced repetition, and a calmer
            interface that keeps one next step in view at a time.
          </p>
        </ContentSection>

        <ContentSection title="Get in touch" icon={Globe}>
          <p className="mb-3">Questions, bug reports, or ideas — any of these work.</p>
          <div className="flex flex-wrap gap-2">
            {socialLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Button key={link.label} variant="outline" size="sm" className="gap-2" asChild>
                  <a href={link.href} target="_blank" rel="noopener noreferrer">
                    <Icon className="h-4 w-4" />
                    {link.label}
                  </a>
                </Button>
              );
            })}
          </div>
        </ContentSection>

        <div className={cn(slateCardClass, "flex flex-wrap items-center justify-between gap-3 p-4 sm:p-5")}>
          <p className={typeBodyClass}>Looking for answers instead?</p>
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate("/faq")}>
            <HelpCircle className="h-4 w-4" />
            Read the FAQ
          </Button>
        </div>
      </ContentPage>
    </>
  );
};

export default About;
